import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { wallIsCartesian, wallIsXCartesian, wallIsYCartesian } from '../checks/checkCartesian'
import { signedDistanceInDirectionOf } from '../distance'
import { roundImmutableVector3 } from '../rounding'

export const XY_LINE_LENGTH_EPSILON = 1e-9

type XYZ = { x: number; y: number; z: number }

const toVector = (point: XYZ): ImmutableVector3 => new ImmutableVector3(point.x, point.y, point.z)

export const lineXYLength = (start: XYZ, end: XYZ): number =>
  Math.hypot(end.x - start.x, end.y - start.y)

export interface ProjectedLinePointInXY {
  projected: ImmutableVector3
  t: number
  lineLength: number
}

/**
 * Projects `point` onto the XY footprint of `start`→`end`, then maps that
 * parameter back onto the 3D line so elevation/slope is preserved.
 * Returns null when the line has no usable XY length (near-vertical).
 */
export const projectPointOntoLineInXY = (
  start: XYZ,
  end: XYZ,
  point: XYZ,
): ProjectedLinePointInXY | null => {
  const xyLength = lineXYLength(start, end)
  if (xyLength < XY_LINE_LENGTH_EPSILON) return null

  const startVec = toVector(start)
  const endVec = toVector(end)
  const startXY = new ImmutableVector3(start.x, start.y, 0)
  const endXY = new ImmutableVector3(end.x, end.y, 0)
  const pointXY = new ImmutableVector3(point.x, point.y, 0)
  const xyDirection = endXY.sub(startXY).normalize()
  const distanceAlongXY = signedDistanceInDirectionOf(startXY, pointXY, xyDirection)
  const t = distanceAlongXY / xyLength
  const projected = startVec.add(endVec.sub(startVec).multiplyScalar(t))

  return {
    projected,
    t,
    lineLength: startVec.distanceTo(endVec),
  }
}

/**
 * Projects a 3D point onto the wall axis and returns the relative position in [0, 1].
 * Same projection logic as in wall split and wall merge.
 */
export const calcRelativePositionOnWallAxis = (wall: ShapeObject, point: Point): number => {
  const points = wall.shape?.points
  if (!points || points.length < 2) return 0
  const p0 = new ImmutableVector3(points[0].x, points[0].y, points[0].z)
  const p1 = new ImmutableVector3(points[1].x, points[1].y, points[1].z)
  const wallAxisDirection = p1.sub(p0).normalize()
  const wallLength = p0.distanceTo(p1)
  if (wallLength <= 0) return 0
  const pointVec = new ImmutableVector3(point.x, point.y, point.z)
  const distanceAlongAxis = signedDistanceInDirectionOf(p0, pointVec, wallAxisDirection)
  const relative = distanceAlongAxis / wallLength
  return relative
}

/**
 * Projects a rip start point onto a target wall axis.
 * Returns the relative position in [0, 1], or null if outside the wall segment.
 */
export const projectRipOntoWall = (wall: ShapeObject, ripStart: Point): number | null => {
  const relativePosition = calcRelativePositionOnWallAxis(wall, ripStart)
  if (relativePosition < 0 || relativePosition > 1) {
    return null
  }
  return relativePosition
}

export const calculatePointAlongWallAxisFromRelativePosition = (
  wall: ShapeObject,
  relativePosition: number,
) => {
  const points = wall.shape?.points
  if (!points || points.length < 2) return new ImmutableVector3(0, 0, 0)
  const p0 = new ImmutableVector3(points[0].x, points[0].y, points[0].z)
  const p1 = new ImmutableVector3(points[1].x, points[1].y, points[1].z)
  const wallAxisDirection = p1.sub(p0).normalize()
  const wallLength = p0.distanceTo(p1)
  if (wallLength <= 0) return new ImmutableVector3(0, 0, 0)
  const point = p0.addScaledVector(wallAxisDirection, relativePosition * wallLength)

  if (wallIsCartesian(wall)) {
    const numDecimalsRounding = 10
    const roundedPoint = roundImmutableVector3(point, numDecimalsRounding)
    return roundedPoint
  }
  return point
}

export const projectPointOntoWallAxisCartesianAware = (
  wall: ShapeObject,
  point: ImmutableVector3,
) => {
  const wallStart = wall.shape.points[0]
  const wallEnd = wall.shape.points[1]
  const wallDirection = wallEnd.sub(wallStart).normalize()

  const isWallXCartesian = wallIsXCartesian(wall)
  const isWallYCartesian = wallIsYCartesian(wall)

  let newStartAlongWallAxis: ImmutableVector3
  if (isWallXCartesian) {
    newStartAlongWallAxis = new ImmutableVector3(point.x, wallStart.y, point.z)
  } else if (isWallYCartesian) {
    newStartAlongWallAxis = new ImmutableVector3(wallStart.x, point.y, point.z)
  } else {
    const signedDistanceFromStart = signedDistanceInDirectionOf(wallStart, point, wallDirection)
    newStartAlongWallAxis = wallStart.addScaledVector(wallDirection, signedDistanceFromStart)
  }

  // hack: the z component can be reused from the global start point
  const newStartProjectedIntoWall = new ImmutableVector3(
    newStartAlongWallAxis.x,
    newStartAlongWallAxis.y,
    point.z,
  )
  return newStartProjectedIntoWall
}
